/**
 * Utility functions for handling file downloads and file viewing
 * Reusable across the application for downloads, attachments, and file management
 */

export const looksLikeUrl = (value) =>
  typeof value === 'string' &&
  (value.startsWith('http://') || value.startsWith('https://') || value.includes('://'));

/**
 * Resolve the HTTP(S) URL to fetch for download from a file object or string.
 * Service/product attachments may store the URL only on `name` (DB) or only on `url` (uploads).
 */
export const getAttachmentDownloadUrl = (file) => {
  if (!file) return '';
  if (typeof file === 'string') return looksLikeUrl(file) ? file : '';
  const candidates = [
    file.url,
    file.file_url,
    file._fileUrl,
    file._downloadUrl,
    file.preview,
    file.name,
  ];
  return candidates.find((c) => c && looksLikeUrl(c)) || '';
};

/**
 * Short display/download filename (not a URL).
 */
export const getAttachmentDisplayName = (file) => {
  if (!file || typeof file === 'string') return null;
  if (file.displayName && !looksLikeUrl(file.displayName)) return file.displayName;
  if (file.name && !looksLikeUrl(file.name)) return file.name;
  return null;
};

const triggerBrowserDownload = (blob, displayFileName) => {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = displayFileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  requestAnimationFrame(() => {
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }, 250);
  });
};

const tryDirectUrlDownload = (fileUrl, displayFileName) => {
  const link = document.createElement('a');
  link.href = fileUrl;
  link.download = displayFileName;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  document.body.appendChild(link);
  requestAnimationFrame(() => {
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
    }, 250);
  });
};

const isValidDownloadBlob = (blob, response) => {
  if (!blob || blob.size === 0) return false;
  const type = (blob.type || response?.headers?.get('content-type') || '').toLowerCase();
  if (type.includes('text/html') || type.includes('application/json')) return false;
  return true;
};

/**
 * Resolve a short, filesystem-safe download name from file metadata or URL.
 * DB/API often stores the full public URL in `name` — avoid using that as the download filename.
 * @param {string} fileUrl - File URL used for download
 * @param {string|null} preferredName - Optional display name from file object
 * @returns {string}
 */
export const resolveDownloadFileName = (fileUrl, preferredName = null) => {
  if (preferredName && !looksLikeUrl(preferredName)) {
    return preferredName.split('/').pop()?.split('?')[0] || preferredName;
  }

  const fromUrl = fileUrl?.split('/').pop()?.split('?')[0] || 'download';
  try {
    return decodeURIComponent(fromUrl.replace(/^\d+_/, ''));
  } catch {
    return fromUrl.replace(/^\d+_/, '');
  }
};

/**
 * Download a file from URL as a blob
 * @param {string} fileUrl - URL of the file to download
 * @param {string} fileName - Name to save the file as, or provide in URL
 * @param {function} onSuccess - Callback function on successful download
 * @param {function} onError - Callback function on error
 * @returns {Promise<void>}
 */
export const downloadFileFromUrl = async (
  fileUrl,
  fileName = null,
  onSuccess = null,
  onError = null
) => {
  const downloadUrl = looksLikeUrl(fileUrl) ? fileUrl : '';
  const displayFileName = resolveDownloadFileName(downloadUrl || fileUrl, fileName);

  try {
    if (!downloadUrl) {
      throw new Error('No valid file URL provided');
    }

    const response = await fetch(downloadUrl, { mode: 'cors' });
    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }
    const blob = await response.blob();

    if (!isValidDownloadBlob(blob, response)) {
      tryDirectUrlDownload(downloadUrl, displayFileName);
      if (onSuccess) onSuccess();
      return;
    }

    triggerBrowserDownload(blob, displayFileName);
    if (onSuccess) onSuccess();
  } catch (error) {
    console.error('Download error:', error);
    try {
      if (downloadUrl) {
        tryDirectUrlDownload(downloadUrl, displayFileName);
        if (onSuccess) onSuccess();
        return;
      }
    } catch (fallbackError) {
      console.error('Direct download fallback failed:', fallbackError);
    }
    if (onError) onError(error);
    throw error;
  }
};

/**
 * Download an image with custom naming
 * @param {string} imageUrl - URL of the image to download
 * @param {string} imageName - Custom name for the image (optional)
 * @param {number} imageIndex - Index for naming purposes
 * @param {function} onSuccess - Callback function on successful download
 * @param {function} onError - Callback function on error
 * @returns {Promise<void>}
 */
export const downloadImage = async (
  imageUrl,
  imageName = null,
  imageIndex = 0,
  onSuccess = null,
  onError = null
) => {
  const downloadUrl = looksLikeUrl(imageUrl) ? imageUrl : '';
  const ext = getFileExtension(downloadUrl || imageUrl) || 'jpg';
  const resolvedName = resolveDownloadFileName(downloadUrl || imageUrl, imageName);
  const fileName =
    resolvedName !== 'download'
      ? resolvedName
      : `image-${imageIndex + 1}-${Date.now()}.${ext}`;

  try {
    if (!downloadUrl) {
      throw new Error('No valid image URL provided');
    }

    const response = await fetch(downloadUrl, { mode: 'cors' });
    if (!response.ok) {
      throw new Error('Image download failed');
    }
    const blob = await response.blob();

    if (!isValidDownloadBlob(blob, response)) {
      tryDirectUrlDownload(downloadUrl, fileName);
      if (onSuccess) onSuccess();
      return;
    }

    triggerBrowserDownload(blob, fileName);
    if (onSuccess) onSuccess();
  } catch (error) {
    console.error('Image download error:', error);
    try {
      if (downloadUrl) {
        tryDirectUrlDownload(downloadUrl, fileName);
        if (onSuccess) onSuccess();
        return;
      }
    } catch (fallbackError) {
      console.error('Image direct download fallback failed:', fallbackError);
    }
    if (onError) onError(error);
    throw error;
  }
};

/**
 * Open a file in a new window with appropriate viewer
 * @param {string} fileUrl - URL of the file
 * @param {object} options - Configuration options
 * @param {boolean} options.isActualPdf - Whether file is PDF
 * @param {boolean} options.isOfficeFile - Whether file is Office format
 * @param {boolean} options.isClient - Whether user is a client
 * @param {boolean} options.hideToolbar - Hide PDF toolbar
 * @param {string} options.fileName - File name for title
 * @returns {void}
 */
export const openFileInViewer = (
  fileUrl,
  options = {}
) => {
  const {
    isActualPdf = false,
    isOfficeFile = false,
    isClient = false,
    hideToolbar = true,
    fileName = 'File',
  } = options;

  try {
    if (!fileUrl) {
      throw new Error('No file URL provided');
    }

    const newWindow = window.open('', '_blank');

    if (isActualPdf) {
      // PDF viewer
      const toolbarParams = hideToolbar
        ? '#toolbar=0&navpanes=0&scrollbar=0'
        : '';

      newWindow.document.write(`
        <html>
          <head>
            <title>PDF Viewer</title>
            <style>
              body { margin: 0; padding: 0; overflow: hidden; }
              iframe { width: 100vw; height: 100vh; border: none; }
            </style>
          </head>
          <body>
            <iframe src="${fileUrl}${toolbarParams}" title="PDF Viewer"></iframe>
          </body>
        </html>
      `);
    } else if (isOfficeFile) {
      // Microsoft Office Online Viewer
      const officeViewerSrc = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
        fileUrl
      )}`;

      newWindow.document.write(`
        <html>
          <head>
            <title>${fileName}</title>
            <style>
              body { margin: 0; padding: 0; overflow: hidden; }
              iframe { width: 100vw; height: 100vh; border: none; }
            </style>
          </head>
          <body>
            <iframe src="${officeViewerSrc}" title="${fileName}"></iframe>
          </body>
        </html>
      `);
    } else {
      // Google Docs Viewer as fallback
      const googleViewerSrc = `https://docs.google.com/gview?url=${encodeURIComponent(
        fileUrl
      )}&embedded=true`;

      newWindow.document.write(`
        <html>
          <head>
            <title>${fileName}</title>
            <style>
              body { margin: 0; padding: 0; overflow: hidden; }
              iframe { width: 100vw; height: 100vh; border: none; }
            </style>
          </head>
          <body>
            <iframe src="${googleViewerSrc}" title="${fileName}"></iframe>
          </body>
        </html>
      `);
    }

    newWindow.document.close();
  } catch (error) {
    console.error('Error opening file viewer:', error);
    throw error;
  }
};

/**
 * Get embedded iframe source for file preview
 * For clients or restricted viewing
 * @param {string} fileUrl - URL of the file
 * @param {object} options - Configuration options
 * @param {boolean} options.isActualPdf - Whether file is PDF
 * @param {string} options.hideToolbar - Hide PDF toolbar parameters
 * @returns {string} - iframe src URL
 */
export const getEmbeddedFileSource = (
  fileUrl,
  options = {}
) => {
  const {
    isActualPdf = false,
    hideToolbar = true,
  } = options;

  if (!fileUrl) return '';

  if (isActualPdf) {
    const toolbarParams = hideToolbar
      ? '&toolbar=0&navpanes=0&scrollbar=0'
      : '';
    return `https://docs.google.com/gview?url=${encodeURIComponent(
      fileUrl
    )}&embedded=true${toolbarParams}`;
  }

  // Default to Google Docs Viewer for other formats
  return `https://docs.google.com/gview?url=${encodeURIComponent(
    fileUrl
  )}&embedded=true`;
};

/**
 * Extract file extension from URL or fileName
 * @param {string} fileUrl - URL or file name
 * @returns {string} - File extension in lowercase
 */
export const getFileExtension = (fileUrl) => {
  if (!fileUrl) return '';
  return fileUrl?.split('/').pop()?.split('?')[0]?.split('.').pop()?.toLowerCase() || '';
};

/**
 * Check if file is an Office document
 * @param {string} fileUrl - URL or file name
 * @returns {boolean} - True if Office file
 */
export const isOfficeFile = (fileUrl) => {
  const ext = getFileExtension(fileUrl);
  return ['xls', 'xlsx', 'doc', 'docx', 'ppt', 'pptx', 'csv'].includes(
    ext
  );
};

/**
 * Check if file is a PDF
 * @param {string} fileUrl - URL or file name
 * @param {string} fileType - MIME type (optional)
 * @returns {boolean} - True if PDF
 */
export const isPdfFile = (fileUrl, fileType = '') => {
  const ext = getFileExtension(fileUrl);
  return (
    ext === 'pdf' ||
    fileType === 'pdf' ||
    fileType === 'application/pdf'
  );
};

/**
 * Check if file is an image
 * @param {string} fileType - MIME type
 * @returns {boolean} - True if image
 */
export const isImageFile = (fileType) => {
  const nonRenderableSubtypes = [
    'eps',
    'ai',
    'ps',
    'psd',
    'x-eps',
    'x-ai',
    'postscript',
  ];
  
  if (nonRenderableSubtypes.includes(fileType?.toLowerCase())) {
    return false;
  }

  return (
    fileType?.startsWith('image/') ||
    fileType === 'img' ||
    fileType === 'image'
  );
};

export default {
  downloadFileFromUrl,
  downloadImage,
  openFileInViewer,
  getEmbeddedFileSource,
  getFileExtension,
  resolveDownloadFileName,
  getAttachmentDownloadUrl,
  getAttachmentDisplayName,
  looksLikeUrl,
  isOfficeFile,
  isPdfFile,
  isImageFile,
};
